import React, { useEffect } from 'react';
import { MOD_KEY, IS_MAC } from '../utils/platform';
import ModalCloseButton from './ModalCloseButton';

/**
 * ShortcutsModal — keyboard cheat-sheet. Opened by `?` (or
 * Shift+/) globally. Walter 2026-05-06 round 7: the app has a
 * handful of power-user keybinds (sidebar toggle on `\`, Ctrl+N for
 * new list, Ctrl+F for search, Ctrl+E for focused view, Esc for
 * close) that have no central documentation. This puts them in
 * one place; opening with `?` is the convention everyone reading
 * a cheat-sheet expects.
 *
 * Modifier glyphs come from utils/platform — Mac users see ⌘,
 * everyone else sees Ctrl.
 */
const SEP = IS_MAC ? '' : '+';
const COMBO = (letter) => `${MOD_KEY}${SEP}${letter}`;

// Walter 2026-05-06 round 8: dropped Ctrl+N from the cheat-sheet —
// browsers on Windows intercept it for "new window" and we can't
// override that. Plain `n` (no modifier, when not in an input)
// replaces it. Cmd+N still works on Mac because not all browsers
// grab it system-level there. `n` is also faster muscle memory.
//
// Walter 2026-05-16: letters lowercase ("Ctrl+f", "Ctrl+e") since
// these refer to actual key characters, and the lowercase 'n' below
// already sets the convention. Also added the Tab row.
const NEW_KEY = IS_MAC ? `${COMBO('n')} or n` : 'n';

// Walter 2026-05-18: alt-glyph follows platform convention. On Mac
// the modifier key is labelled ⌥ ("option"); everywhere else it's
// "Alt". Move-line uses Alt+Shift+↑/↓ on every platform — the
// keyboard event is the same; only the label changes.
const ALT_LABEL = IS_MAC ? '⌥' : 'Alt';
const SHIFT_LABEL = IS_MAC ? '⇧' : 'Shift';
const MOVE_UP = IS_MAC ? `${ALT_LABEL}${SHIFT_LABEL}↑` : `${ALT_LABEL}+${SHIFT_LABEL}+↑`;
const MOVE_DOWN = IS_MAC ? `${ALT_LABEL}${SHIFT_LABEL}↓` : `${ALT_LABEL}+${SHIFT_LABEL}+↓`;

// Walter 2026-06-16: Ctrl/Cmd+F is now an in-list find bar; the cross-board
// results search moved to Shift+Ctrl/Cmd+F.
const SHIFT_F = IS_MAC ? `⇧${MOD_KEY}f` : `Shift+${MOD_KEY}+f`;

// Walter 2026-06-24: line highlights (Ctrl+H cycles a colour) + mark-as-done
// (Ctrl+Shift+H). Same modifier convention as the rest.
const HL = COMBO('h');
const HL_DONE = IS_MAC ? `⇧${MOD_KEY}h` : `Shift+${MOD_KEY}+h`;

const ROWS = [
  { keys: NEW_KEY,        desc: 'new list on the current board' },
  { keys: COMBO('f'),     desc: 'find in this list — or all lists on the board' },
  { keys: SHIFT_F,        desc: 'search across all boards' },
  { keys: COMBO('e'),     desc: 'toggle focused view on the active list' },
  { keys: 'Tab',          desc: 'cycle focus through open lists (Shift+Tab reverses)' },
  { keys: MOVE_UP,        desc: 'move current line up (works on selected block too)' },
  { keys: MOVE_DOWN,      desc: 'move current line down' },
  { keys: HL,             desc: 'cycle line highlight (none → green → blue → red)' },
  { keys: HL_DONE,        desc: 'mark line as done — greys it out (toggle)' },
  { keys: '\\',           desc: 'show / hide the left sidebar' },
  { keys: 'Esc',          desc: 'close the open modal / panel / popover' },
  { keys: '?',            desc: 'this cheat-sheet' },
];

export default function ShortcutsModal({ open, onClose }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.55)',
        zIndex: 2300,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: 'lists-fadein 0.12s ease',
        padding: '20px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border-bright)',
          borderRadius: 'var(--radius)',
          maxWidth: 400, width: '100%',
          padding: '20px 22px',
          boxShadow: 'var(--shadow-modal-mid)',
          fontFamily: 'var(--mono)',
          color: 'var(--text)',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: 14,
        }}>
          <span style={{
            fontSize: 'calc(13px * var(--ui-scale, 1))',
            fontWeight: 600,
          }}>
            keyboard shortcuts
          </span>
          <ModalCloseButton onClick={onClose} style={{ position: 'static' }} />
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          rowGap: 8, columnGap: 14,
          fontSize: 'calc(11px * var(--ui-scale, 1))',
        }}>
          {ROWS.map((row, i) => (
            <React.Fragment key={i}>
              <kbd style={{
                fontFamily: 'var(--mono)',
                fontSize: 'calc(11px * var(--ui-scale, 1))',
                padding: '2px 8px',
                background: 'var(--surface2)',
                border: '1px solid var(--border-bright)',
                borderRadius: 'var(--radius)',
                color: 'var(--accent)',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                textAlign: 'center',
                minWidth: 36,
              }}>{row.keys}</kbd>
              <span style={{ color: 'var(--text-dim)', alignSelf: 'center' }}>{row.desc}</span>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}
